<dialog #dialog [id]="modalId" class="modal">
  <div class="modal-box vr-modal max-w-3xl bg-base-100">
    <form *ngIf="form" [formGroup]="form" (ngSubmit)="submit()">
      <header class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-primary">
          {{ isCreate() ? 'Create' : 'Edit' }}
          {{ mode?.kind === 'cross' ? 'Cross-Field Rule' : 'Field Rule' }}
        </h2>
        <button
          type="button"
          (click)="close()"
          class="text-base-content/70 hover:text-base-content"
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <!-- Common fields -->
      <section class="space-y-3 mb-4">
        <!-- Template selectors -->
        <ng-container *ngIf="!isCrossField()">
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">
              <span class="text-error font-bold">*</span> Field / Template
            </span>
            <ng-select
              formControlName="metadata_template"
              [items]="templatesSignal()"
              bindLabel="name"
              bindValue="id"
              [clearable]="false"
              [searchable]="true"
              placeholder="Pick a field/template"
            >
              <ng-template ng-option-tmp let-item="item">
                <div class="text-sm font-medium">{{ item.name }}</div>
                <div class="text-xs text-base-content/70">
                  {{ fieldTypeLabel(item.data_type) }} · {{ item.key_name }} ·
                  {{ item.source_type_name || 'Core' }}
                </div>
              </ng-template>
            </ng-select>
            <span
              *ngIf="selectedTemplate() as t"
              class="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-info/10 text-info text-xs font-medium"
            >
              Type: {{ fieldTypeLabel(t.data_type) }}
            </span>
          </label>
        </ng-container>

        <ng-container *ngIf="isCrossField()">
          <!-- Source Type goes first because the Dependent/Governing field
               options depend on it (Core + that source type's templates). -->
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">
              Source Type
              <span class="text-base-content/60 text-xs ml-1">
                (blank = Core only)
              </span>
            </span>
            <ng-select
              formControlName="source_type"
              [items]="sourceTypesSignal()"
              bindLabel="name"
              bindValue="id"
              [clearable]="true"
              [searchable]="true"
              placeholder="Core only"
            ></ng-select>
          </label>

          <div class="grid grid-cols-2 gap-3">
            <label class="block">
              <span class="text-sm font-medium text-primary-focus mb-1 block">
                <span class="text-error font-bold">*</span> Dependent Field
                <span
                  class="text-base-content/60 ml-1 cursor-help"
                  title="The field being validated — the one a user must adjust to satisfy this rule (e.g. City)."
                  >(?)</span
                >
              </span>
              <ng-select
                formControlName="left_template"
                [items]="crossFieldTemplatesSignal()"
                bindLabel="name"
                bindValue="id"
                [clearable]="false"
                [searchable]="true"
                placeholder="Pick the field to validate"
              >
                <ng-template ng-option-tmp let-item="item">
                  <div class="text-sm font-medium">{{ item.name }}</div>
                  <div class="text-xs text-base-content/70">
                    {{ fieldTypeLabel(item.data_type) }} · {{ item.key_name }} ·
                    {{ item.source_type_name || 'Core' }}
                  </div>
                </ng-template>
              </ng-select>
              <span
                *ngIf="leftSelectedTemplate() as t"
                class="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-info/10 text-info text-xs font-medium"
              >
                Type: {{ fieldTypeLabel(t.data_type) }}
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-primary-focus mb-1 block">
                <span class="text-error font-bold">*</span> Governing Field
                <span
                  class="text-base-content/60 ml-1 cursor-help"
                  title="The reference field this rule checks against — the one that governs the dependent field (e.g. State)."
                  >(?)</span
                >
              </span>
              <ng-select
                formControlName="right_template"
                [items]="crossFieldTemplatesSignal()"
                bindLabel="name"
                bindValue="id"
                [clearable]="false"
                [searchable]="true"
                placeholder="Pick the field to validate against"
              >
                <ng-template ng-option-tmp let-item="item">
                  <div class="text-sm font-medium">{{ item.name }}</div>
                  <div class="text-xs text-base-content/70">
                    {{ fieldTypeLabel(item.data_type) }} · {{ item.key_name }} ·
                    {{ item.source_type_name || 'Core' }}
                  </div>
                </ng-template>
              </ng-select>
              <span
                *ngIf="rightSelectedTemplate() as t"
                class="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-info/10 text-info text-xs font-medium"
              >
                Type: {{ fieldTypeLabel(t.data_type) }}
              </span>
            </label>
          </div>

          <p class="text-xs text-base-content/70 -mt-1">
            The <span class="font-medium">Dependent Field</span> is validated against the
            <span class="font-medium">Governing Field</span>. Example: to make
            <span class="font-medium">City</span> depend on
            <span class="font-medium">State</span>, pick City as Dependent and State as Governing.
          </p>

          <!-- Live plain-English preview of the rule as configured -->
          <div
            *ngIf="crossFieldPreview() as preview"
            class="rounded border border-info/30 bg-info/5 px-3 py-2"
          >
            <p class="text-xs font-semibold text-info uppercase tracking-wide mb-0.5">Preview</p>
            <p class="text-sm text-primary">{{ preview }}</p>
          </div>
        </ng-container>

        <!-- Rule type + severity row -->
        <div class="grid grid-cols-2 gap-3">
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">
              <span class="text-error font-bold">*</span> Rule Type
            </span>
            <ng-select
              formControlName="rule_type"
              [items]="scopedCatalog()"
              bindLabel="rule_type"
              bindValue="rule_type"
              [clearable]="false"
              [searchable]="true"
              placeholder="Select rule type"
            >
              <ng-template ng-option-tmp let-item="item">
                <div class="text-sm font-medium">{{ item.rule_type }}</div>
                <div class="text-xs text-base-content/70" *ngIf="item.description">
                  {{ item.description }}
                </div>
              </ng-template>
            </ng-select>
          </label>
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">Severity</span>
            <ng-select
              formControlName="severity"
              [items]="SEVERITY_OPTIONS"
              [clearable]="false"
            >
              <ng-template ng-label-tmp let-item="item">
                <span
                  class="badge badge-sm"
                  [class.badge-error]="item === 'ERROR'"
                  [class.badge-warning]="item === 'WARNING'"
                >
                  {{ item }}
                </span>
              </ng-template>
              <ng-template ng-option-tmp let-item="item">
                <span
                  class="badge badge-sm"
                  [class.badge-error]="item === 'ERROR'"
                  [class.badge-warning]="item === 'WARNING'"
                >
                  {{ item }}
                </span>
              </ng-template>
            </ng-select>
          </label>
        </div>

        <!-- Optional commons -->
        <div class="grid grid-cols-2 gap-3">
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">Sort Order</span>
            <input
              type="number"
              formControlName="sort_order"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />
          </label>
          <label class="block" *ngIf="!isCrossField()">
            <span class="text-sm font-medium text-primary-focus mb-1 block">
              Rule Group
              <span
                class="text-base-content/60 ml-1"
                title="Rules sharing a non-empty group form an OR-set: the field passes the group iff at least one rule in the group passes."
                >(?)</span
              >
            </span>
            <input
              type="text"
              formControlName="rule_group"
              placeholder="optional"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />
          </label>
        </div>

        <label class="block">
          <span class="text-sm font-medium text-primary-focus mb-1 block">Message Template (optional)</span>
          <input
            type="text"
            formControlName="message_template"
            placeholder="Override the default error message. Supports {field_name}, {value}, {min}, {max}."
            class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
          />
        </label>

        <label class="flex items-center gap-2">
          <input type="checkbox" formControlName="is_active" class="checkbox checkbox-sm" />
          <span class="text-sm font-medium text-primary-focus">Active</span>
        </label>
      </section>

      <!-- Dynamic params (schema-driven from the catalog's param_defs) -->
      <section
        *ngIf="paramDefsSignal().length > 0"
        formGroupName="params"
        class="border-t border-base-300 pt-4 space-y-3"
      >
        <h3 class="text-sm font-bold text-primary uppercase tracking-wide mb-2">Params</h3>

        <!-- Operators row: SELECT + BOOLEAN params in a 2-col grid -->
        <div
          *ngIf="operatorParamDefs().length > 0"
          class="grid grid-cols-2 gap-3"
        >
          <ng-container *ngFor="let def of operatorParamDefs()">
            <ng-container *ngTemplateOutlet="paramControl; context: { def: def }"></ng-container>
          </ng-container>
        </div>

        <!-- Rest of the params, full width -->
        <ng-container *ngFor="let def of otherParamDefs()">
          <ng-container *ngTemplateOutlet="paramControl; context: { def: def }"></ng-container>
        </ng-container>

        <!-- Per-def widget renderer. `def` comes from the ngTemplateOutlet context. -->
        <ng-template #paramControl let-def="def">
          <label class="block">
            <span class="text-sm font-medium text-primary-focus mb-1 block">
              <span *ngIf="def.is_required" class="text-error font-bold">*</span>
              {{ def.label || def.key }}
              <span
                *ngIf="def.help_text"
                class="text-base-content/60 ml-1 cursor-help"
                [title]="def.help_text"
                >(?)</span
              >
            </span>

            <!-- SELECT -->
            <ng-select
              *ngIf="def.widget_type === 'SELECT'"
              [formControlName]="def.key"
              [items]="def.choices"
              [clearable]="!def.is_required"
            ></ng-select>

            <!-- MULTI_SELECT -->
            <ng-select
              *ngIf="def.widget_type === 'MULTI_SELECT'"
              [formControlName]="def.key"
              [items]="def.choices"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="!def.is_required"
            ></ng-select>

            <!-- TEMPLATE_OPTION_SELECT (choices from selected template) -->
            <ng-select
              *ngIf="def.widget_type === 'TEMPLATE_OPTION_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="selectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [clearable]="!def.is_required"
              [searchable]="true"
              [placeholder]="selectedTemplate() ? 'Pick a value from the template' : 'Pick a template first'"
            ></ng-select>
            <input
              *ngIf="def.widget_type === 'TEMPLATE_OPTION_SELECT' && !hasTemplateOptions(def.widget_type)"
              type="text"
              [formControlName]="def.key"
              [placeholder]="selectedTemplate() ? 'Type the value to match' : 'Pick a template first'"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />

            <!-- TEMPLATE_OPTION_MULTI_SELECT (choices from selected template) -->
            <ng-select
              *ngIf="def.widget_type === 'TEMPLATE_OPTION_MULTI_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="selectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="!def.is_required"
              [placeholder]="selectedTemplate() ? 'Pick values from the template' : 'Pick a template first'"
            ></ng-select>
            <ng-select
              *ngIf="def.widget_type === 'TEMPLATE_OPTION_MULTI_SELECT' && !hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="[]"
              [multiple]="true"
              [addTag]="true"
              [hideSelected]="true"
              [placeholder]="selectedTemplate() ? 'Type a value and press Enter' : 'Pick a template first'"
            ></ng-select>

            <!-- LEFT_TEMPLATE_OPTION_SELECT (cross-field: items from left_template) -->
            <ng-select
              *ngIf="def.widget_type === 'LEFT_TEMPLATE_OPTION_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="leftSelectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [clearable]="!def.is_required"
              [searchable]="true"
              [placeholder]="leftSelectedTemplate() ? 'Pick a value from the Dependent Field' : 'Pick a Dependent Field first'"
            ></ng-select>
            <input
              *ngIf="def.widget_type === 'LEFT_TEMPLATE_OPTION_SELECT' && !hasTemplateOptions(def.widget_type)"
              type="text"
              [formControlName]="def.key"
              [placeholder]="leftSelectedTemplate() ? 'Type the value to match in the Dependent Field' : 'Pick a Dependent Field first'"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />

            <!-- LEFT_TEMPLATE_OPTION_MULTI_SELECT -->
            <ng-select
              *ngIf="def.widget_type === 'LEFT_TEMPLATE_OPTION_MULTI_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="leftSelectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="!def.is_required"
              [placeholder]="leftSelectedTemplate() ? 'Pick values from the Dependent Field' : 'Pick a Dependent Field first'"
            ></ng-select>
            <ng-select
              *ngIf="def.widget_type === 'LEFT_TEMPLATE_OPTION_MULTI_SELECT' && !hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="[]"
              [multiple]="true"
              [addTag]="true"
              [hideSelected]="true"
              [placeholder]="leftSelectedTemplate() ? 'Type a value and press Enter' : 'Pick a Dependent Field first'"
            ></ng-select>

            <!-- RIGHT_TEMPLATE_OPTION_SELECT (cross-field: items from right_template) -->
            <ng-select
              *ngIf="def.widget_type === 'RIGHT_TEMPLATE_OPTION_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="rightSelectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [clearable]="!def.is_required"
              [searchable]="true"
              [placeholder]="rightSelectedTemplate() ? 'Pick a value from the Governing Field' : 'Pick a Governing Field first'"
            ></ng-select>
            <input
              *ngIf="def.widget_type === 'RIGHT_TEMPLATE_OPTION_SELECT' && !hasTemplateOptions(def.widget_type)"
              type="text"
              [formControlName]="def.key"
              [placeholder]="rightSelectedTemplate() ? 'Type the value to match in the Governing Field' : 'Pick a Governing Field first'"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />

            <!-- RIGHT_TEMPLATE_OPTION_MULTI_SELECT -->
            <ng-select
              *ngIf="def.widget_type === 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT' && hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="rightSelectedTemplate()?.options ?? []"
              bindLabel="value"
              bindValue="value"
              [multiple]="true"
              [closeOnSelect]="false"
              [clearable]="!def.is_required"
              [placeholder]="rightSelectedTemplate() ? 'Pick values from the Governing Field' : 'Pick a Governing Field first'"
            ></ng-select>
            <ng-select
              *ngIf="def.widget_type === 'RIGHT_TEMPLATE_OPTION_MULTI_SELECT' && !hasTemplateOptions(def.widget_type)"
              [formControlName]="def.key"
              [items]="[]"
              [multiple]="true"
              [addTag]="true"
              [hideSelected]="true"
              [placeholder]="rightSelectedTemplate() ? 'Type a value and press Enter' : 'Pick a Governing Field first'"
            ></ng-select>

            <!-- TAGS (free-form list) -->
            <ng-select
              *ngIf="def.widget_type === 'TAGS'"
              [formControlName]="def.key"
              [items]="def.choices"
              [multiple]="true"
              [addTag]="true"
              [hideSelected]="true"
              placeholder="Type a value and press Enter"
            ></ng-select>

            <!-- REGEX_TEXTAREA -->
            <textarea
              *ngIf="def.widget_type === 'REGEX_TEXTAREA'"
              [formControlName]="def.key"
              rows="2"
              placeholder="e.g. ^[A-Z]{3}-[0-9]{4}$"
              class="textarea textarea-bordered w-full text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            ></textarea>

            <!-- NUMBER -->
            <input
              *ngIf="def.widget_type === 'NUMBER'"
              type="number"
              [formControlName]="def.key"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />

            <!-- BOOLEAN -->
            <input
              *ngIf="def.widget_type === 'BOOLEAN'"
              type="checkbox"
              [formControlName]="def.key"
              class="checkbox checkbox-sm"
            />

            <!-- TEXT (default) -->
            <input
              *ngIf="def.widget_type === 'TEXT'"
              type="text"
              [formControlName]="def.key"
              class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
            />
          </label>
        </ng-template>
      </section>

      <!-- Client-side validation summary -->
      <div *ngIf="formLevelErrors().length > 0" class="mt-4 p-3 bg-red-50 border border-red-200 rounded">
        <p class="text-xs font-semibold text-red-800 mb-1">Fix before submitting:</p>
        <ul class="text-xs text-red-700 list-disc list-inside">
          <li *ngFor="let err of formLevelErrors()">{{ err }}</li>
        </ul>
      </div>

      <footer class="flex justify-end gap-2 mt-6 pt-4 border-t border-base-300">
        <button
          type="button"
          (click)="close()"
          class="h-9 px-4 border border-base-300 rounded text-sm text-dark font-medium hover:bg-base-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="form.invalid || formLevelErrors().length > 0"
          class="h-9 px-6 border border-base-300 rounded text-sm text-dark font-medium hover:bg-info hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isCreate() ? 'Create' : 'Save' }}
        </button>
      </footer>
    </form>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button (click)="close()">close</button>
  </form>
</dialog>
